import { Loader2 } from 'lucide-react';
import { CONNECTOR_GRADIENTS, STAGE_LOADING_COPY } from '../constants';
import type { StageVM, StageId } from '../types';
import { useCognition } from '../context';
import { PipelineNode, type NodeState } from '../components/pipeline-node';
import { StageCard } from '../components/stage-card';
import { StageDetail } from '../components/stage-detail';

// The signature reasoning pipeline (DC §7): stage nodes joined by per-segment
// teal->violet gradient connectors, each card expandable to its detail panel.
export function PipelineSection({
  stages,
  expandedStage,
  onToggleStage,
  completedStages,
  currentLoadingStage,
  liveLoadingMessage = '',
  selectedModelIds,
  onAsk,
}: {
  stages: StageVM[];
  expandedStage: string | null;
  onToggleStage: (id: string) => void;
  completedStages: Set<string>;
  currentLoadingStage: string | null;
  liveLoadingMessage?: string;
  selectedModelIds: string[];
  onAsk: (question: string) => void;
}) {
  const { isPreview, isStreaming, isComplete } = useCognition();

  const sub = isStreaming
    ? 'Watch each model reason through your query in real time.'
    : isPreview
    ? 'Preview stages. Run cognition to populate real data.'
    : 'Click any stage to expand the detailed analysis.';

  const nodeStateFor = (id: string): NodeState => {
    if (isStreaming) {
      if (completedStages.has(id)) return 'done';
      if (currentLoadingStage === id) return 'active';
      return 'pending';
    }
    return 'complete';
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 18,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h3 style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-.01em', color: 'var(--foreground)', margin: '0 0 4px' }}>
            AI reasoning breakdown
          </h3>
          <p style={{ fontSize: 14, color: 'var(--muted-foreground)', margin: 0 }}>{sub}</p>
        </div>
        {isStreaming && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              height: 32,
              padding: '0 13px',
              borderRadius: 99,
              background: 'var(--spectral-violet-10)',
              color: 'var(--spectral-violet)',
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            <Loader2 style={{ width: 15, height: 15 }} className="animate-spin" />
            Reasoning in progress…
          </span>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {stages.map((s, idx) => {
          const expandable = isComplete;
          const expanded = expandedStage === s.id;
          const active = isStreaming && currentLoadingStage === s.id;

          return (
            <div
              key={s.id}
              id={`stage-${s.id}`}
              style={{ position: 'relative', display: 'flex', gap: 18, alignItems: 'flex-start' }}
            >
              {idx < stages.length - 1 && (
                <div
                  style={{
                    position: 'absolute',
                    left: 21,
                    top: 26,
                    bottom: -44,
                    width: 2,
                    background: CONNECTOR_GRADIENTS[idx] || CONNECTOR_GRADIENTS[CONNECTOR_GRADIENTS.length - 1],
                    borderRadius: 2,
                    zIndex: 0,
                  }}
                />
              )}

              <PipelineNode num={s.num} accent={s.accent} state={nodeStateFor(s.id)} />

              <StageCard
                stage={s}
                active={active}
                expandable={expandable}
                expanded={expanded}
                showLoading={active}
                loadingMsg={(active && liveLoadingMessage) || STAGE_LOADING_COPY[s.id as StageId] || 'Analyzing…'}
                showPriority={isComplete}
                onToggle={() => onToggleStage(s.id)}
              >
                <StageDetail stage={s} selectedModelIds={selectedModelIds} onAsk={onAsk} />
              </StageCard>
            </div>
          );
        })}
      </div>
    </div>
  );
}
